import { Link } from "wouter";
import { ToolContainer } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { ExternalLink, FileEdit } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";

interface GovForm {
  name: string;
  agency: string;
  description: string;
  officialUrl: string;
}

// Links point to each agency's own stable "About this form" page, which
// always points to the current version -- safer than us hosting or linking
// a specific PDF revision that could go stale.
const FORMS: GovForm[] = [
  {
    name: "Form W-4",
    agency: "IRS",
    description: "Employee's Withholding Certificate — tells your employer how much federal tax to withhold from your paycheck.",
    officialUrl: "https://www.irs.gov/forms-pubs/about-form-w-4",
  },
  {
    name: "Form W-9",
    agency: "IRS",
    description: "Request for Taxpayer Identification Number — used by contractors/freelancers to provide tax info to a business.",
    officialUrl: "https://www.irs.gov/forms-pubs/about-form-w-9",
  },
  {
    name: "Form 1099-NEC",
    agency: "IRS",
    description: "Nonemployee Compensation — used by businesses to report payments made to independent contractors.",
    officialUrl: "https://www.irs.gov/forms-pubs/about-form-1099-nec",
  },
  {
    name: "Form I-9",
    agency: "USCIS",
    description: "Employment Eligibility Verification — required for every new hire in the US to confirm identity and work authorization.",
    officialUrl: "https://www.uscis.gov/i-9",
  },
  {
    name: "Form SS-4",
    agency: "IRS",
    description: "Application for Employer Identification Number (EIN) — needed to hire employees, open a business bank account or file business taxes.",
    officialUrl: "https://www.irs.gov/forms-pubs/about-form-ss-4",
  },
  {
    name: "Form W-2",
    agency: "IRS",
    description: "Wage and Tax Statement — employers use it to report an employee's wages and the taxes withheld.",
    officialUrl: "https://www.irs.gov/forms-pubs/about-form-w-2",
  },
  {
    name: "Form 1040",
    agency: "IRS",
    description: "U.S. Individual Income Tax Return — the main annual federal income tax return for individuals.",
    officialUrl: "https://www.irs.gov/forms-pubs/about-form-1040",
  },
  {
    name: "Schedule C (Form 1040)",
    agency: "IRS",
    description: "Profit or Loss From Business — sole proprietors and single-member LLCs report business income and expenses here.",
    officialUrl: "https://www.irs.gov/forms-pubs/about-schedule-c-form-1040",
  },
  {
    name: "Form 4868",
    agency: "IRS",
    description: "Application for Automatic Extension of Time to File — gives most individuals six more months to file (not to pay).",
    officialUrl: "https://www.irs.gov/forms-pubs/about-form-4868",
  },
  {
    name: "Form 8822",
    agency: "IRS",
    description: "Change of Address — tells the IRS you have moved so notices and refunds reach you.",
    officialUrl: "https://www.irs.gov/forms-pubs/about-form-8822",
  },
];

const TOOL_INFO_CONTENT = {
  what: `A curated collection of links to official, commonly-needed US government forms (like W-4, W-9, and I-9), so you don't have to search for the correct official source yourself.`,
  how: `Scroll to the form you need and open it from the View Official Form button, which goes to the agency's own page. Download it there, then use the PDF Editor link on this page to type into your copy. Nothing is generated or uploaded here.`,
  tip: `Always download tax and legal forms directly from the official .gov source linked here rather than a third-party site — official sources are guaranteed to have the current version, while copies elsewhere can be outdated.`,
};

export default function GovernmentFormsHub() {
  // This page is a static directory of external links -- nothing is
  // generated, computed, or downloaded here, so there's no real
  // "use" of a daily-limited deliverable to log or gate. The
  // useLogToolUsage(true, 3000) call that lived here was a
  // misapplication of the hook (hardcoded true, not tied to any real
  // result), not a gating gap to close -- removed rather than wired
  // up, since there's nothing this tool actually delivers to block.
  return (
    <ToolContainer>
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="p-4 rounded-lg border bg-primary/5">
            <p className="text-sm">
              These are official government forms. We link directly to each agency's own page so you always get
              the current, correct version — we don't host or modify these forms ourselves.
              Once downloaded, you can use our{" "}
              <Link href="/tool/pdf-editor" className="text-primary underline">PDF Editor</Link>{" "}
              to fill it in and save your own copy.
            </p>
          </div>

          <div className="space-y-3">
            {FORMS.map(form => (
              <div key={form.name} className="p-4 rounded-lg border bg-card">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="font-medium text-sm">{form.name}</p>
                    <p className="text-xs text-muted-foreground">{form.agency}</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{form.description}</p>
                <div className="flex gap-2">
                  <Button asChild size="sm" variant="outline" className="gap-1.5">
                    <a href={form.officialUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3.5 w-3.5" /> View Official Form
                    </a>
                  </Button>
                  <Button asChild size="sm" variant="ghost" className="gap-1.5">
                    <Link href="/tool/pdf-editor">
                      <FileEdit className="h-3.5 w-3.5" /> Fill in PDF Editor
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} disclaimer="This page only links to forms published by the issuing agencies; we do not host, change or file them. It is not legal or tax advice. Forms and rules change, so check the agency page for the current version and instructions, and ask a tax professional or attorney if you are unsure which form applies." />
    </ToolContainer>
  );
}
